import createBookingModuleMachine, {
  MentorCheckoutInfo,
} from '@/components/molecules/AvailabilitySelectionModule/AvailabilitySelectionModule.machine';
import { assign, createMachine } from 'xstate';
import { makeCreatePendingSession } from './helpers';
import generateOnboardingURL from '@/utils/generateOnboardingUrl';

export type OnePageCheckoutMachineContext = {
  mentor_info: MentorCheckoutInfo;
  slot: string;
  duration: 30 | 60;
  access_token?: string;
  stripe_client_secret?: string;
  checkout_error?: string;
  authentication_error?: string;
  redirect_url: string;
};

export type SignupEvent = {
  type: 'SIGNUP';
  data: {
    email: string;
    given_name: string;
    family_name: string;
  };
};

export type CheckoutErrorEvent = {
  type: 'CHECKOUT_ERROR';
  data: { error: string };
};

export type OnePageCheckoutMachineEvent =
  | SignupEvent
  | CheckoutErrorEvent
  | { type: 'DISMISS_ERROR' }
  | { type: 'CHECKOUT' }
  | { type: 'CHANGE_BOOKING' }
  | { type: 'NOT_AUTHENTICATED' }
  | { type: 'AUTHENTICATED' }
  | { type: 'LOGIN' };

const onePageCheckoutMachineCreator = ({
  mentor_info,
  AvailabilitySelectionMachine,
  createPendingSession,
}: {
  mentor_info: MentorCheckoutInfo;
  AvailabilitySelectionMachine: ReturnType<
    typeof createBookingModuleMachine
  >;
  createPendingSession: ReturnType<typeof makeCreatePendingSession>;
}) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QHkB2YAKBDGBhAFmAMYDWA9gK4AuAdAMpgA2xVAggG5YCWjWARjy5UAngGIIZdDS6p2ZEmBpZOPfoJEB9WExZdJAbQAMAXUSgADmVhC9qMyAAeiAIwBmGoYAsADleGATN4ArP5env6uADQgwoiu-gCcNH7+QQDsrt7ezp4JzgBsngC+RdFomDhgBMTk1PRcUKgU5jSsuLgAohgAKhoAIh3drACSADJ0onTDAOIAcgCqGEamSCCW1lS29k4IQb40-vmprgn5zoYJ8Z7RsQj5CZ40wWm++fFvD-klZejYeISkSi0OgNJotNqdHr9QYjcaiXAACVYs2mHQ0ACFkMgANLDFHLezrGySbYuF40I57BKGLwZZwJU43RD+TyGA6HYL5EJpfK074gcp-KoA2rA0HNVrtLq9AZDMYTUbIaZ4gmrImbEmrHZufxPbwvB6s7yHTzOfxMhBpTyPbz5LLnM65Zx7fmCyrVQF1bDCAC2YFQtFwACUOqxunjphoIch5rNuuJJIoZHIFDQiAAnMBYKiYf0QGRQBiwawGEyEqzEuxalyhbwUk6+IIXQz6woWpv5ZJpQxvVw850ZBKu37ukVAmjev0ByWQmUw+XwhEdXDYmPdVUWCsaqugbWeK00IKXfw5BL+NIhfzmmKIK1pGhpB6uI6FbIJIKuYcVf41ceT-20B0QZBsgQaiH0wx0AAspBdAaEBIFBhuaxbls1YIM42Sdp4ITZLkhiuKy74Wkc95hK4QSngElxfkKHqihOWC+gBNCIsuuIohoa6Luxa7wcBoHIeqaG7mSDyHp4bzPOergUVEN6WqkHj5GkLxBGcEQXP4JSlCAqBkBAcD2G6P6elQ5YbCJjiIAAtBknaFLJGR7NyXgWjZnY0oYOS8iELauD5tGjr+dQMMwRBsCovACIwQi3JulmaqJCDhBa2Q0LkCT6i2OSJDyQWmQxIKNM0FmVqSCAeVkFKeE5FHBOebkKUEuQ0M6zhWn2LwvC2BXCiFYoleCUpQrKsJ0GV24VZcyTeDSaT0oYGReAkaQkc+FKUTyPY4QUzp9fR47FWCNAhhBIa4L0mI4hGk1WTsHmsjVdUuY11wKd41r1qt577m4mFpAdY5ekxU7mWqqFJdZlo5AchQPHkvJ9j2FqmnW2VnIUVr3M4bhAwNjHMdOwahuGnHRrG3R3VDOw8rqJoIwUBHdvkFoLZ2C0XPcPhc-kXy6SZ-VmYTYMztK0JyuM1M7tDZq2sk57qdazoXBRbNPSe5ypEeQTqWa+PC-+04IaB0sVfSWUZSeu0PKp163FauoLbjrKsvS5yAwLI6FX+oMsWxK4Rlx8xUxDiUy9qZz3tkj6fcaBQLfbiDZO4biETq7zds4OlFEAA */
      context: {
        mentor_info,
        slot: '',
        duration: mentor_info.availabilityDetails.halfHour ? 30 : 60,
        redirect_url: `${process.env.NEXT_PUBLIC_WEBAPP_URL}/onboarding/mentee`,
      },
      id: 'OnePageCheckout',
      initial: 'SelectAvailability',
      states: {
        SelectAvailability: {
          invoke: {
            src: 'AvailabilitySelectionMachine',
            id: 'availability_selection',
            onDone: [
              {
                target: 'Signup',
                actions: [
                  assign({
                    slot: (_, event) => event.data.selected,
                    duration: (_, event) => event.data.duration,
                  }),
                  (context, event) => {
                    const payload = {
                      session: {
                        duration: event.data.duration,
                        slot: event.data.selected,
                      },
                      mentor: {
                        name: `${context.mentor_info.first_name} ${context.mentor_info.last_name}`,
                        avatar_url:
                          context.mentor_info.profile_image_url, // TODO: pass the mentor's avatar url into the context so it's available here
                      },
                    };
                    const serializedPayload = JSON.stringify(payload);
                    localStorage.setItem(
                      'success_metadata',
                      serializedPayload
                    );
                  },
                ],
              },
            ],
          },
        },
        Signup: {
          initial: 'ACCEPT_DETAILS',
          states: {
            ACCEPT_DETAILS: {
              on: {
                SIGNUP: '#OnePageCheckout.Payment',
                CHANGE_BOOKING: {
                  target: '#OnePageCheckout.SelectAvailability',
                },
                LOGIN: {
                  target: 'REDIRECT_BOOKING',
                },
              },
            },

            REDIRECT_BOOKING: {
              type: 'final',
            },
          },
        },
        Payment: {
          initial: 'CREATING_ACCOUNT',
          states: {
            CREATING_ACCOUNT: {
              invoke: {
                src: 'createPendingSession',
                id: 'createPendingSession',
                onDone: [
                  {
                    target: 'ACCEPT_DETAILS',
                    actions: [
                      assign((context, event) => ({
                        stripe_client_secret:
                          event.data.stripe_client_secret,
                        family_name: event.data.family_name,
                        given_name: event.data.given_name,
                        pending_session_id:
                          event.data.pending_session_id,
                      })),
                      (_, event) => {
                        const url = generateOnboardingURL(event.data);
                        localStorage.setItem('onboarding_url', url);
                      },
                    ],
                  },
                ],
                onError: [
                  {
                    target: 'ERROR',
                    actions: assign((_, event) => ({
                      checkout_error:
                        event.data?.message ||
                        'Failed to create account. Please try again.',
                    })),
                  },
                ],
              },
            },

            ACCEPT_DETAILS: {
              on: {
                CHECKOUT: 'CHECKING_OUT',
              },
            },
            ERROR: {
              on: {
                DISMISS_ERROR: {
                  target: 'ACCEPT_DETAILS',
                },
              },
            },
            CHECKING_OUT: {
              on: {
                CHECKOUT_ERROR: {
                  target: 'ERROR',
                  actions: assign((_, event: CheckoutErrorEvent) => ({
                    checkout_error: event.data.error,
                  })),
                },
              },
            },
          },
        },
      },
      schema: {
        events: {} as OnePageCheckoutMachineEvent,
        context: {} as OnePageCheckoutMachineContext,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
    },
    {
      actions: {},
      services: {
        AvailabilitySelectionMachine,
        createPendingSession,
      },
      guards: {},
      delays: {},
    }
  );

export default onePageCheckoutMachineCreator;
